import dayjs, { Dayjs } from 'dayjs';

/**
 * Type casting utilities for converting ERP values to application field formats
 */

// Application field types
type AppFieldType = 'str' | 'int' | 'float' | 'datetime' | 'bool';

// Salesforce field types that we commonly encounter
type SalesforceFieldType = 'string' | 'id' | 'boolean' | 'int' | 'double' | 'currency' | 'percent' | 'date' | 'datetime' | 'reference' | 'picklist' | 'textarea' | 'phone' | 'email' | 'url';

/**
 * Cast a value from ERP format to application field format
 */
export function castToAppType(value: any, targetType: AppFieldType, sourceType?: string): any {
    if (value === null || value === undefined) {
        return null;
    }

    switch (targetType) {
        case 'str':
            return castToString(value);
        case 'int':
            return castToInt(value);
        case 'float':
            return castToFloat(value);
        case 'datetime':
            return castToDatetime(value);
        case 'bool':
            return castToBool(value);
        default:
            return value;
    }
}

/**
 * Cast value to string
 */
function castToString(value: any): string {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (dayjs.isDayjs(value)) {
        return (value as Dayjs).format('YYYY-MM-DD');
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

/**
 * Cast value to integer
 */
function castToInt(value: any): number | null {
    if (typeof value === 'number') {
        return Math.round(value);
    }
    if (typeof value === 'string') {
        // Remove any formatting characters (commas, spaces, currency symbols)
        const cleaned = value.replace(/[,$\s€£¥]/g, '');
        const parsed = parseInt(cleaned, 10);
        return isNaN(parsed) ? null : parsed;
    }
    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }
    return null;
}

/**
 * Cast value to float
 */
function castToFloat(value: any): number | null {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string') {
        // Remove any formatting characters (commas, spaces, currency symbols)
        const cleaned = value.replace(/[,$\s€£¥]/g, '');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? null : parsed;
    }
    if (typeof value === 'boolean') {
        return value ? 1.0 : 0.0;
    }
    return null;
}

/**
 * Cast value to datetime (dayjs object)
 */
function castToDatetime(value: any): Dayjs | null {
    if (dayjs.isDayjs(value)) {
        return value as Dayjs;
    }
    if (value instanceof Date) {
        return dayjs(value);
    }
    if (typeof value === 'string') {
        // Try parsing common date formats
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : null;
    }
    if (typeof value === 'number') {
        // Assume Unix timestamp (milliseconds)
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : null;
    }
    return null;
}

/**
 * Cast value to boolean
 */
function castToBool(value: any): boolean {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        const lower = value.toLowerCase().trim();
        // Handle common truthy strings
        if (['true', 'yes', '1', 'on', 'oui', 'vrai'].includes(lower)) {
            return true;
        }
        // Handle common falsy strings
        if (['false', 'no', '0', 'off', 'non', 'faux', ''].includes(lower)) {
            return false;
        }
        // Non-empty string is truthy
        return lower.length > 0;
    }
    if (typeof value === 'number') {
        return value !== 0;
    }
    return Boolean(value);
}

/**
 * Format a value for display based on its type
 */
export function formatValueForDisplay(value: any, type?: AppFieldType): string {
    if (value === null || value === undefined) {
        return '(empty)';
    }

    if (type === 'datetime' || dayjs.isDayjs(value)) {
        const dayjsValue = dayjs.isDayjs(value) ? value : dayjs(value);
        return dayjsValue.isValid() ? dayjsValue.format('YYYY-MM-DD') : String(value);
    }

    if (type === 'bool' || typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
}

/**
 * Check if two values are equal, considering type differences
 */
export function areValuesEqual(appValue: any, erpValue: any, appType?: AppFieldType): boolean {
    // Handle null/undefined
    if (appValue === null || appValue === undefined) {
        return erpValue === null || erpValue === undefined;
    }
    if (erpValue === null || erpValue === undefined) {
        return false;
    }

    // For datetime, compare as formatted strings
    if (appType === 'datetime' || dayjs.isDayjs(appValue)) {
        const appDate = dayjs.isDayjs(appValue) ? appValue : dayjs(appValue);
        const erpDate = dayjs.isDayjs(erpValue) ? erpValue : dayjs(erpValue);

        if (!appDate.isValid() || !erpDate.isValid()) {
            return false;
        }

        return appDate.format('YYYY-MM-DD') === erpDate.format('YYYY-MM-DD');
    }

    // For booleans, normalize both values
    if (appType === 'bool') {
        return castToBool(appValue) === castToBool(erpValue);
    }

    // For numbers, compare numerically
    if (appType === 'int' || appType === 'float') {
        const appNum = typeof appValue === 'number' ? appValue : parseFloat(String(appValue));
        const erpNum = typeof erpValue === 'number' ? erpValue : parseFloat(String(erpValue));

        if (isNaN(appNum) || isNaN(erpNum)) {
            return String(appValue) === String(erpValue);
        }

        return appType === 'int'
            ? Math.round(appNum) === Math.round(erpNum)
            : Math.abs(appNum - erpNum) < 0.0001; // Float comparison with tolerance
    }

    // Default: string comparison
    return String(appValue) === String(erpValue);
}
